/**
 * Paragraph Style XML Service
 *
 * Persists ParagraphStyleRecord[] to a Word.CustomXmlPart embedded in the .docx.
 * This gives style history durability across add-in close/reopen without an
 * external store — the data travels with the document.
 *
 * Namespace: http://manuscript-styling-system/para-style-history/v1
 *
 * @see services/paragraph-style-history-service.ts  (data types)
 * @see style_history/StyleHistoryContext.tsx         (consumer)
 */

/* global Word */

import type { ParagraphStyleRecord, StyleChangeEntry, StyleChangeSource } from "src/services";

const NS = "http://manuscript-styling-system/para-style-history/v1";
const EMPTY_XML = `<?xml version="1.0" encoding="UTF-8"?><styleHistory xmlns="${NS}"></styleHistory>`;

// ────────────────────────────────────────────────────────────────────────────
// Serialization
// ────────────────────────────────────────────────────────────────────────────

function serializeRecords(records: ParagraphStyleRecord[]): string {
  const doc = document.implementation.createDocument(NS, "styleHistory", null);
  const root = doc.documentElement;

  for (const record of records) {
    const el = doc.createElementNS(NS, "record");
    el.setAttribute("index", String(record.index));
    el.setAttribute("uniqueLocalId", record.uniqueLocalId);
    el.setAttribute("paragraphTag", record.paragraphTag);
    el.setAttribute("originalStyle", record.originalStyle);
    el.setAttribute("currentStyle", record.currentStyle);
    el.setAttribute("trackedChangeCount", String(record.trackedChangeCount));
    el.setAttribute("isPending", String(record.isPending));
    el.setAttribute("locked", String(record.locked));

    for (const entry of record.styleHistory) {
      const entryEl = doc.createElementNS(NS, "entry");
      entryEl.setAttribute("previousStyle", entry.previousStyle ?? "");
      entryEl.setAttribute("newStyle", entry.newStyle);
      entryEl.setAttribute("source", entry.source);
      entryEl.setAttribute("timestamp", entry.timestamp);
      el.appendChild(entryEl);
    }

    root.appendChild(el);
  }

  return new XMLSerializer().serializeToString(doc);
}

function deserializeRecords(xml: string): ParagraphStyleRecord[] {
  const parsed = new DOMParser().parseFromString(xml, "application/xml");

  const parseError = parsed.querySelector("parsererror");
  if (parseError) {
    console.error("[StyleHistoryXml] XML parse error:", parseError.textContent);
    return [];
  }

  const records: ParagraphStyleRecord[] = [];
  const recordElements = parsed.getElementsByTagNameNS(NS, "record");

  for (const el of Array.from(recordElements)) {
    const styleHistory: StyleChangeEntry[] = [];

    for (const entryEl of Array.from(el.getElementsByTagNameNS(NS, "entry"))) {
      styleHistory.push({
        previousStyle: entryEl.getAttribute("previousStyle") ?? "",
        newStyle: entryEl.getAttribute("newStyle") ?? "",
        source: (entryEl.getAttribute("source") ?? "initial") as StyleChangeSource,
        timestamp: entryEl.getAttribute("timestamp") ?? "",
      });
    }

    records.push({
      index: Number(el.getAttribute("index")),
      uniqueLocalId: el.getAttribute("uniqueLocalId") ?? "",
      paragraphTag: el.getAttribute("paragraphTag") ?? "",
      originalStyle: el.getAttribute("originalStyle") ?? "",
      currentStyle: el.getAttribute("currentStyle") ?? "",
      trackedChangeCount: Number(el.getAttribute("trackedChangeCount") ?? 0),
      isPending: el.getAttribute("isPending") === "true",
      locked: el.getAttribute("locked") === "true",
      styleHistory,
    });
  }

  return records;
}

// ────────────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────────────

/**
 * Creates the style history CustomXmlPart in the document if it doesn't exist.
 * Idempotent — safe to call on every startup.
 */
export async function initStyleHistoryXmlPart(): Promise<void> {
  try {
    await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(NS);
      parts.load("items");
      await context.sync();

      if (parts.items.length === 0) {
        context.document.customXmlParts.add(EMPTY_XML);
        await context.sync();
        return;
      }

      if (parts.items.length === 1) {
        return;
      }

      // Multiple parts accumulated — scan all, keep the one with the most records.
      const xmlResults = parts.items.map((part) => part.getXml());
      await context.sync();

      let canonicalIndex = 0;
      let canonicalRecordCount = 0;
      for (let i = 0; i < xmlResults.length; i++) {
        const parsed = new DOMParser().parseFromString(xmlResults[i].value, "application/xml");
        const count = parsed.getElementsByTagNameNS(NS, "record").length;
        if (count > canonicalRecordCount) {
          canonicalIndex = i;
          canonicalRecordCount = count;
        }
      }

      // Delete all non-canonical parts. CustomXmlPart.delete() persists through
      // save/reopen in the Word JS API (WordApi 1.4+). The previous setXml(EMPTY_XML)
      // approach was a no-op: blanked parts still carried the namespace and were
      // found again by getByNamespace on every subsequent startup.
      for (let i = 0; i < parts.items.length; i++) {
        if (i !== canonicalIndex) {
          parts.items[i].delete();
        }
      }
      await context.sync();
    });
  } catch (error) {
    console.error(`[StyleHistoryXml] Init error:`, error);
    throw new Error(
      `Failed to init style history XML part: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

// Serializes writes so concurrent callers never fight over the same XML part.
// Each call chains onto the previous one; errors are caught on the chain tail
// so a single failed write does not block subsequent writes.
let writeChain: Promise<void> = Promise.resolve();

/**
 * Serializes and writes ParagraphStyleRecord[] to the document's CustomXmlPart.
 * Assumes initStyleHistoryXmlPart() has already run — throws if no part exists.
 * Calls are queued and executed one at a time.
 */
export function persistStyleRecords(records: ParagraphStyleRecord[]): Promise<void> {
  const task = writeChain.then(() => doPersist(records));
  writeChain = task.catch(() => {});
  return task;
}

async function doPersist(records: ParagraphStyleRecord[]): Promise<void> {
  const xml = serializeRecords(records);

  try {
    await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(NS);
      parts.load("items");
      await context.sync();

      const partCount = parts.items.length;
      if (partCount === 0) {
        // initStyleHistoryXmlPart() is the sole creator of this part and must have
        // run before React mounts. Reaching here means that invariant was violated.
        throw new Error(
          `[StyleHistoryXml] Invariant violation: no XML part found for namespace "${NS}". ` +
            `initStyleHistoryXmlPart() must be called before any persist operation.`
        );
      }

      // Write to items[0]. Delete any unexpected duplicates — CustomXmlPart.delete()
      // persists through save/reopen in the Word JS API (WordApi 1.4+).
      parts.items[0].setXml(xml);
      for (let i = 1; i < partCount; i++) {
        parts.items[i].delete();
        console.warn(
          `[StyleHistoryXml] Persist: deleted unexpected duplicate XML part at index ${i}`
        );
      }

      await context.sync();
    });
  } catch (error) {
    console.error(`[StyleHistoryXml] Persist error:`, error);
    throw new Error(
      `Failed to persist style records: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Reads and deserializes ParagraphStyleRecord[] from the document's CustomXmlPart.
 * Returns [] if no part exists or the part contains no records.
 */
export async function loadStyleRecords(): Promise<ParagraphStyleRecord[]> {
  try {
    return await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(NS);
      parts.load("items");
      await context.sync();

      const partCount = parts.items.length;
      if (partCount === 0) {
        return [];
      }
      if (partCount > 1) {
        console.warn(
          `[StyleHistoryXml] Load: found ${partCount} XML parts — expected exactly 1. Reading items[0].`
        );
      }

      const xmlResult = parts.items[0].getXml();
      await context.sync();

      return deserializeRecords(xmlResult.value);
    });
  } catch (error) {
    console.error(`[StyleHistoryXml] Load error:`, error);
    throw new Error(
      `Failed to load style records: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
